import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';

export const useWorkspaceInvitations = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.workspaceInvitations(workspaceId ?? ''),
    queryFn: () => container.workspaces.listInvitations(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
};

export const useInviteMember = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, roleId }: { email: string; roleId: string }) =>
      container.workspaces.inviteMember(workspaceId, email, roleId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaceInvitations(workspaceId) }),
  });
};

export const useRevokeInvitation = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) =>
      container.workspaces.revokeInvitation(workspaceId, invitationId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaceInvitations(workspaceId) }),
  });
};

export const useInvitation = (token: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.invitation(token ?? ''),
    queryFn: () => container.workspaces.getInvitation(token as string),
    enabled: Boolean(token),
    retry: false,
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => container.workspaces.acceptInvitation(token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces }),
  });
};

export const useDeclineInvitation = () => {
  return useMutation({
    mutationFn: (token: string) => container.workspaces.declineInvitation(token),
  });
};
